// API for the Fleet pages (/fleet…) — forklifts, vehicles, and 90 days of
// inspections. Every endpoint is Admin / Fleet Manager only and identifies
// the caller by the x-user-id header (no token validation server-side — the
// same trust model as the PCW and feature request admin routes).
// Server: server/routes/forklifts/api/fleetHandler.js.

export const FLEET_ROLES = ["Admin", "Fleet Manager"];

const request = async (url, userId, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-user-id": userId || "",
      ...(options.headers || {}),
    },
  });
  let content = {};
  try {
    content = await res.json();
  } catch (e) {
    // Non-JSON error page (e.g. the server is restarting).
  }
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};

const queryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

// ---- Overview (Fleet home + homepage widget) ----

// todayStart is the viewer's local midnight so "today" means their today.
export const fetchFleetOverview = (userId, todayStart) =>
  request(
    `/api/fleet/overview${queryString({ todayStart: todayStart?.toISOString() })}`,
    userId
  );

// ---- Forklifts ----

export const fetchForklifts = (userId) => request("/api/fleet/forklifts", userId);

export const fetchForklift = (userId, id) =>
  request(`/api/fleet/forklifts/${encodeURIComponent(id)}`, userId);

// { unit, location, moffett }
export const addForklift = (userId, forklift) =>
  request("/api/fleet/forklifts", userId, {
    method: "POST",
    body: JSON.stringify(forklift),
  });

// Any of { unit, location, moffett }. Service status has its own call.
export const updateForklift = (userId, id, changes) =>
  request(`/api/fleet/forklifts/${encodeURIComponent(id)}`, userId, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });

// { inService, reason, note, expectedBack } — going out of service needs a
// reason (SERVICE_REASONS in components/Fleet/fleetFormat.js).
export const setForkliftService = (userId, id, change) =>
  request(`/api/fleet/forklifts/${encodeURIComponent(id)}/service`, userId, {
    method: "POST",
    body: JSON.stringify(change),
  });

export const deleteForklift = (userId, id) =>
  request(`/api/fleet/forklifts/${encodeURIComponent(id)}`, userId, {
    method: "DELETE",
  });

// ---- Locations (Location Manager) ----

// Every location with its contacts, shifts, alert thresholds and forklift
// counts.
export const fetchLocations = (userId) => request("/api/fleet/locations", userId);

// { location, forklifts } — the location plus its forklifts with stats.
export const fetchLocation = (userId, id) =>
  request(`/api/fleet/locations/${encodeURIComponent(id)}`, userId);

export const addLocation = (userId, location) =>
  request("/api/fleet/locations", userId, {
    method: "POST",
    body: JSON.stringify({ location }),
  });

// Any of managerName/Phone/Email, generalName/Phone/Email, emailAlert,
// textAlert, shifts ([{ id, name, managerName, managerEmail, managerPhone }]).
export const updateLocation = (userId, id, changes) =>
  request(`/api/fleet/locations/${encodeURIComponent(id)}`, userId, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });

// moveTo is required when the location still has forklifts.
export const deleteLocation = (userId, id, { moveTo } = {}) =>
  request(`/api/fleet/locations/${encodeURIComponent(id)}${queryString({ moveTo })}`, userId, {
    method: "DELETE",
  });

// ---- Vehicles ----

// { vehicles, removed, gpsError, fetchedAt } — `vehicles` leaves out trucks
// a Fleet Manager removed; those come back in `removed`.
export const fetchVehicles = (userId) => request("/api/fleet/vehicles", userId);

// key is the Bistrack VehicleID, or "gps-<tracker id>" for a GPS unit with
// no vehicle record. Opens removed trucks too (vehicle.hidden). `branches`
// lists the branches a truck can be moved to.
export const fetchVehicle = (userId, key) =>
  request(`/api/fleet/vehicles/${encodeURIComponent(key)}`, userId);

// Any of { name, branch, hidden } — only the fields sent change. "" for
// name or branch goes back to Bistrack's. Bistrack itself isn't changed
// (the edit lives in forks.vehicle_overrides, shared with the mobile app).
// Resolves to { key, changed, vehicle }; a name another truck already has
// fails with that message (409).
export const updateVehicle = (userId, key, changes) =>
  request(`/api/fleet/vehicles/${encodeURIComponent(key)}`, userId, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });

// Same shape as setForkliftService.
export const setVehicleService = (userId, key, change) =>
  request(`/api/fleet/vehicles/${encodeURIComponent(key)}/service`, userId, {
    method: "POST",
    body: JSON.stringify(change),
  });

// ---- Inspections ----

// Summaries for every forklift and vehicle inspection in the range (the
// server clamps it to the last 90 days), newest first.
// detail: true adds every checklist answer (used by Export CSV).
export const fetchInspections = (userId, { type, branch, from, to, detail } = {}) =>
  request(
    `/api/fleet/inspections${queryString({ type, branch, from, to, detail: detail ? "1" : "" })}`,
    userId
  );

// Full checklist for one inspection from the log.
export const fetchInspection = (userId, { type, source, id }) =>
  request(
    `/api/fleet/inspections/${encodeURIComponent(type)}/${encodeURIComponent(
      source
    )}/${encodeURIComponent(id)}`,
    userId
  );

// ---- Kiosk (public, no sign-in) ----

// The branch kiosk's board: forklifts, contacts, shifts, and each unit's
// latest check-in per shift. `aliasShift` is set when `branch` is an old
// per-shift location (sbc2) that now belongs to a merged branch.
export const fetchKioskBoard = async (branch) => {
  const res = await fetch(`/api/fleet/forklift/kiosk${queryString({ branch })}`);
  const content = await res.json();
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};

// { id, inService, reason, note, by } — the kiosk has no signed-in user, so
// `by` is whatever name the operator typed.
export const setKioskService = async ({ id, inService, reason, note, by }) => {
  const res = await fetch("/api/fleet/forklift/updateService", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ id, inService, reason, note, by }),
  });
  if (!res.ok) throw new Error("Couldn't update the unit");
};

// A unit's last 90 days of check-ins from both apps, with full checklists.
export const fetchKioskHistory = async ({ unit, branch }) => {
  const res = await fetch(
    `/api/fleet/forklift/history${queryString({ unit, branch })}`
  );
  const content = await res.json();
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};
